import dayjs from 'dayjs';
import React, { useEffect } from 'react';

import { DateFilterMenuContent } from './date-filter-menu-content';
import { isCompleteFilter, useDateFilterState } from './date-filter-utilities';
import { DateFilterType } from './date-filter.types';
import { Card } from '../card/card';
import { FilterChip } from '../filter-chip/filter-chip';

export interface DateFilterChipProps {
  /** The label to display on the chip. e.g. 'Due Date' */
  label: string;
  /** The current filter value */
  value?: DateFilterType;
  /** Controls if the chip is opened */
  isOpen?: boolean;
  /** Callback to trigger when the filter changes */
  onChange: (filter: DateFilterType) => void;
  /** Callback to trigger when the filter is applied */
  onApply: (filter: DateFilterType) => void;
  /** Callback to trigger x button being clicked */
  onClear?: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const DateFilterChip: React.FC<DateFilterChipProps> = ({
  onChange,
  onApply,
  onClear,
  label,
  value,
  isOpen = false,
}) => {
  const { filter, reset, resetTempFilter, apply, updateTempFilter } = useDateFilterState(onChange, onApply, value);

  useEffect(() => {
    if (isCompleteFilter(filter.tempFilter) && filter.flushTemp) {
      apply();
    }
  }, [apply, filter.tempFilter, filter.flushTemp]);

  const selectionText = (): string => {
    let selectionText = '';
    switch (filter.appliedFilter.option) {
      case 'between': {
        if (filter.appliedFilter.startDate && filter.appliedFilter.endDate) {
          selectionText =
            dayjs(filter.appliedFilter.startDate).format('M/D/YYYY') +
            ' - ' +
            dayjs(filter.appliedFilter.endDate).format('M/D/YYYY');
        }
        break;
      }
      case 'within last': {
        let period = filter.appliedFilter.period as string;
        if (filter.appliedFilter.periodQuantity && filter.appliedFilter.periodQuantity <= 1) {
          // Remove pluralization for singular quantity. Since all of our valid periods end in 's'
          // (days, weeks, months, years) we can simply slice the last character. We should use a
          // pluralization library if we need more advanced pluralization in the future
          period = period?.slice(0, -1);
        }
        selectionText = `within last ${filter.appliedFilter.periodQuantity} ${period}`;
        break;
      }
      case 'after': {
        if (filter.appliedFilter.startDate) {
          selectionText = `after ${dayjs(filter.appliedFilter.startDate).format('M/D/YYYY')}`;
        }
        break;
      }
      case 'before': {
        if (filter.appliedFilter.endDate) {
          selectionText = `before ${dayjs(filter.appliedFilter.endDate).format('M/D/YYYY')}`;
        }
        break;
      }
    }
    return selectionText;
  };
  return (
    <FilterChip
      isOpen={isOpen}
      onOpen={() => {
        if (isCompleteFilter(filter.appliedFilter)) {
          // Otherwise, update the temp filter with the applied filter data to ensure we show
          // the currently applied filter on open
          updateTempFilter({ ...filter.appliedFilter });
        } else {
          // If we don't have a completely filled out applied filter, reset the temp filter to clear the form
          resetTempFilter();
        }
      }}
      menuContent={
        <Card shadow="dark">
          <DateFilterMenuContent
            onChange={(filter) => {
              updateTempFilter(filter);
            }}
            onOptionChange={(option) => {
              // Reset our temp filter first to clear other filters options
              resetTempFilter();
              updateTempFilter({ option: option });
            }}
            dateFilter={filter.tempFilter}
          />
        </Card>
      }
      onClearSelection={() => {
        reset();
        onClear?.();
      }}
      value={label}
      firstSelection={selectionText()}
      numberSelected={isCompleteFilter(filter.appliedFilter) ? 1 : 0}
    />
  );
};

export default DateFilterChip;
